import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, Loader2, Upload } from 'lucide-react';
import { MapCamera } from './MapCamera';
import { api } from '../../../services/api';
import {
  ACCEPTED_HINT,
  UploadRefused,
  describeUploadError,
  toUploadableImage,
  uploadProblem,
  withUploadType,
} from '../bmpUpload';

/**
 * Upload BMP-map sheets from the site page, any time (Sean Anderson,
 * feedback 2026-10-08: "upload maps anytime for any project and it reset the
 * counter off the last upload"). Same endpoint and file rules as the gate;
 * every upload is a new map row, so the next ask comes four routine
 * inspections after it. The sheets land in the Documents list as Updated.
 */
export function BmpMapUploadButton({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  // Jim 2026-10-14: photos through the guided camera; files still attach as before
  const [showCamera, setShowCamera] = useState(false);
  const cameraUnavailable = useCallback(() => {
    setShowCamera(false);
    inputRef.current?.click();
  }, []);

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const problem = files.map(uploadProblem).find(Boolean);
      if (problem) throw new UploadRefused(problem);
      const form = new FormData();
      for (const file of files) {
        form.append('files', await toUploadableImage(withUploadType(file)));
      }
      // Explicit multipart header: the api instance defaults to application/json.
      const r = await api.post<{ sheet_count?: number }>(
        `/sites/${siteId}/bmp-map`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return { count: files.length, data: r.data };
    },
    onSuccess: ({ count }) => {
      setNote({
        ok: true,
        text: `${count} ${count === 1 ? 'sheet' : 'sheets'} saved. The next map ask is four routine inspections from now.`,
      });
      qc.invalidateQueries({ queryKey: ['site-documents', siteId] });
      qc.invalidateQueries({ queryKey: ['bmp-map-status', siteId] });
    },
    onError: (err) => setNote({ ok: false, text: describeUploadError(err) }),
  });

  return (
    <div className="flex flex-col items-end gap-1">
      {showCamera && (
        <MapCamera
          onDone={(files) => {
            setShowCamera(false);
            if (files.length) {
              setNote(null);
              upload.mutate(files);
            }
          }}
          onCancel={() => setShowCamera(false)}
          onUnavailable={cameraUnavailable}
        />
      )}
      <button
        type="button"
        onClick={() => setShowCamera(true)}
        disabled={upload.isPending}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 disabled:opacity-60"
      >
        <Camera className="w-3.5 h-3.5" />
        Photograph BMP map
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        aria-label="Upload BMP map"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) {
            setNote(null);
            upload.mutate(files);
          }
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={upload.isPending}
        title={ACCEPTED_HINT}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 disabled:opacity-60"
      >
        {upload.isPending ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Upload className="w-3.5 h-3.5" />
        )}
        {upload.isPending ? 'Uploading…' : 'Upload BMP map'}
      </button>
      {note && (
        <p
          className={`text-xs text-right ${
            note.ok
              ? 'text-emerald-700 dark:text-emerald-300'
              : 'text-red-600 dark:text-red-400'
          }`}
        >
          {note.text}
        </p>
      )}
    </div>
  );
}
